import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { omit } from 'lodash';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { PreOrderTimeSlot } from '@rbi-ctg/menu';
import { IStore } from '@rbi-ctg/store';
import { ActionLink } from 'components/action-button';
import { MobileSpacer } from 'components/mobile-spacer';
import { Modal, ModalContent } from 'components/modal';
import { PreOrderTimeModal, PreOrderTimeModalModeConst } from 'components/preorder-time-modal';
import { GraphQLErrorMessages } from 'enums/graphql';
import { useSendUpdateUserAttributesEventMutation } from 'generated/graphql-gateway';
import {
  DeliveryStatus,
  IDeliveryAddress,
  IRestaurantNode,
  useDeliveryRestaurantLazyQuery,
} from 'generated/rbi-graphql';
import { MapProviders } from 'hooks/geolocation/enum';
import { usePlaceIdDetails } from 'hooks/geolocation/use-place-id-details';
import { usePlaceIdTomTomDetails } from 'hooks/geolocation/use-place-id-tomtom-details';
import { AddressFormatType, useAddressFormat } from 'hooks/use-address-format';
import { useDialogModal } from 'hooks/use-dialog-modal';
import { IErrorModal, useErrorModal } from 'hooks/use-error-modal';
import { usePlacesService } from 'hooks/use-map/use-places-service';
import {
  DeliveryStoreStatusToStoreStatusMap,
  IGetClosestAvailableDeliveryRestaurantResult,
  StoreStatus,
  useQueryClosestAvailableDeliveryRestaurant,
} from 'remote/api/restaurants';
import { useAuthContext } from 'state/auth';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { useNetworkContext } from 'state/network';
import { ServiceMode, useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { convertMilesToMeters } from 'utils/distance';
import { getCountry, platform } from 'utils/environment';
import { IPlaceData } from 'utils/geolocation';
import { parseGeocodeResult } from 'utils/geolocation/parse-geocode-result';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';
import { fadeIn } from 'utils/style/animations';
import { toast } from 'utils/toast';

import { StoreLocatorSteps } from '../../pages/store-locator/types';

import { DeleteDeliveryAddress } from './delete-delivery-address';
import { useDeliveryAddressModalContext } from './delivery-address-modal.context';
import { DeliveryClosedAtMomentContainer } from './delivery-closed-at-moment-container';
import { DeliveryPausedContainer } from './delivery-paused-container';
import { DeliveryUnavailable } from './delivery-unavailable';
import { DeliveryAddressForm } from './new-address-form/delivery-address-form';
import { SaveDeliveryAddress } from './save-delivery-address';
import { OtherLocation } from './save-delivery-address/other-location/index';
import { SelectLocation } from './save-delivery-address/select-location';
import { useSaveDeliveryAddress } from './save-delivery-address/use-save-delivery-address';
import { ActionStatus, AddressModalAction, SelectedDeliveryAddress } from './types';

// iOS HACK to add spacing to the bottom of the form.
// Needed because the keyboard was covering the form inputs
const SpacerContainer = styled.div`
  display: block;
  width: 100%;
`;

const ModalContentWrapper = styled(ModalContent)`
  & div[class*='modal-content__Inner'] {
    height: 100%;
    /* overwrite modal content set width for delivery address styling */
    width: 100%;
    max-width: 100%;
  }
`;

export const FadeInWrapper = styled.div`
  margin: 0 1rem;
  animation: 0.5s ${fadeIn} forwards;
`;

const DeliveryInPageContainer = styled.div<{ notAvailable: boolean }>`
  position: relative;
  margin-block-start: -1rem;

  ${Styles.desktop} {
    margin-block-start: ${(p: any) => (p.notAvailable ? '5rem' : '-1rem')};
  }
`;

export const formatPhoneNumber = (phoneNumber: string) => {
  // removes all non-digit characters
  return phoneNumber.replace(/[^\d]/g, '');
};

interface IDeliveryAddressModalProps {
  redirectUrl: string;
  dismissAddressModalUrl: string;
  currentStep: StoreLocatorSteps;
  setCurrentStep: (view: StoreLocatorSteps) => void;
  /**
   * This flag forces the Delivery modal to render in the same page and not open as a separate modal
   */
  renderInPage?: boolean;
  editedAddress?: IDeliveryAddress | null;
  setEditedAddress: (address: IDeliveryAddress) => void;
}

interface AddressModalState {
  address: SelectedDeliveryAddress | null;
  error: IErrorModal | null;
  status: ActionStatus;
}

const addressReducer = (state: AddressModalState, action: AddressModalAction) => {
  switch (action.type) {
    case ActionStatus.IDLE:
      return { ...state, address: null, error: null, status: action.type };
    case ActionStatus.QUOTE_UNAVAILABLE: {
      return { ...state, error: null, status: action.type };
    }
    case ActionStatus.PENDING: {
      return { ...state, address: action.address, error: null, status: ActionStatus.PENDING };
    }
    case ActionStatus.ERROR: {
      return { ...state, error: action.error, status: ActionStatus.ERROR };
    }
    case ActionStatus.DELIVERY_CLOSED_AT_MOMENT: {
      return { ...state, error: null, status: action.type };
    }
    case ActionStatus.DELIVERY_PAUSED: {
      return { ...state, error: action.error, status: action.type };
    }
    default: {
      throw new Error('Unrecognized state transition');
    }
  }
};

const getAddressLabel = ({ address }: SelectedDeliveryAddress) =>
  `${address.addressLine1}, ${address.city}, ${address.state} ${
    address.zip
  }, ${address.country?.toLocaleUpperCase()}`;

export const DeliveryAddressModal: React.FC<IDeliveryAddressModalProps> = ({
  redirectUrl,
  dismissAddressModalUrl,
  currentStep,
  setCurrentStep,
  renderInPage,
  editedAddress,
  setEditedAddress,
}) => {
  const [state, dispatch] = useReducer(addressReducer, {
    error: null,
    address: null,
    status: ActionStatus.IDLE,
  });
  const { isAuthenticated, user } = useAuthContext();
  const { logNavigationClick, trackEvent, updateUserAttributes } = useCdpContext();
  const [sendUpdateUserAttributesEvent] = useSendUpdateUserAttributesEventMutation();
  const order = useOrderContext();
  const { isDelivery, setDeliverySurchargeFee } = useServiceModeContext();
  const {
    setDeliveryAddress: setSaveDeliveryAddress,
    deliveryAddress,
    canSaveDeliveryAddress,
    dispatchDeliveryAddressState,
    setDeliveryAddressMapFallback,
    setShowMapFallback,
    deliveryAddressState: {
      showDeliveryAddressOtherLocation,
      showSaveDeliveryAddress,
      showSaveDeliveryAddressWithLocation,
      showDeleteDeliveryAddress,
      showDeliveryAddress,
    },
  } = useDeliveryAddressModalContext();
  const { formatMessage } = useIntl();
  const { connection } = useNetworkContext();
  const { getTomTomDetails } = usePlaceIdTomTomDetails();
  const { checkStaticMenuItemAvailability, selectedStaticMenuItemId } = useMenuContext();
  const addressFormat = useAddressFormat();

  const [nextOpenDate, setNextDateOpen] = useState(new Date());
  const [skipSavedAddressClosedStore, setSkipSavedAddressClosedStore] = useState(false);
  const [lastEventQuoteMessage, setLastEventQuoteMessage] = useState('');
  const [shouldShowScheduleModal, setShouldShowScheduleModal] = useState(false);
  const [deliveryPreOrderTimeSlots, setDeliveryPreOrderTimeSlots] = useState<
    PreOrderTimeSlot[] | null
  >(null);
  const [preOrderRestaurant, setPreOrderRestaurant] = useState<IStore | null>(null);

  const enableSavedAddressesImprovements = useFlag(
    LaunchDarklyFlag.ENABLE_SAVED_ADDRESSES_IMPROVEMENTS
  );

  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);

  const enableConsolidatedDeliveryRestaurantLogic = useFlag(
    LaunchDarklyFlag.ENABLE_CONSOLIDATED_DELIVERY_RESTAURANT_BACKEND_LOGIC
  );

  const enableThirdPartyRedirect = useFlag(
    LaunchDarklyFlag.ENABLE_THIRD_PARTY_REDIRECT_UNAVAILABLE_DELIVERY_MODAL
  );

  const enableDeliveryClosedAtMoment = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_CLOSED_AT_MOMENT);
  const enableDeliveryPaused = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY_PAUSED);

  const [queryDeliveryRestaurant, { data: deliveryRestaurantData }] =
    useDeliveryRestaurantLazyQuery({ fetchPolicy: 'no-cache' });

  const [queryRestaurantsFromBackend, storeData, deliveryQuoteError] =
    useQueryClosestAvailableDeliveryRestaurant();

  const { addressProvider, setPreselectedPreOrderTimeSlot } = useStoreContext();
  const placesService = usePlacesService();
  const getDetails = usePlaceIdDetails();
  const navigate = useNavigate();
  const enableSaveDeliveryAddress = useFlag(LaunchDarklyFlag.ENABLE_SAVE_DELIVERY_ADDRESS);

  const handleSavedDismiss = useCallback(() => {
    navigate(routes.menu);
  }, [navigate]);

  const addressModalIdle = useCallback(() => {
    dispatch({ type: ActionStatus.IDLE });
  }, []);

  const { saveDeliveryAddress } = useSaveDeliveryAddress({
    onClose: handleSavedDismiss,
    onSaveDeliveryAddress: addressModalIdle,
  });

  const enableUserSavedDeliveryAddressPhone = useFlag(
    LaunchDarklyFlag.ENABLE_USER_SAVED_DELIVERY_ADDRESS_PHONE
  );

  const enableMapFallBack = useFlag(LaunchDarklyFlag.ENABLE_MAP_FALLBACK);
  const enablePluConfigs = useFlag(LaunchDarklyFlag.ENABLE_PLU_CONFIGS);
  const shouldEnableUserSavedDeliveryAddressPhone = enableUserSavedDeliveryAddressPhone && !!user;

  const showDeliveryAddresses =
    shouldEnableUserSavedDeliveryAddressPhone && Boolean(user?.details?.deliveryAddresses?.length);

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Delivery Not Completed',
  });

  const [AlertDialog, openAlert, alertMessage] = useDialogModal({
    onConfirm: () => navigate(`${routes.account}/info`),
    modalAppearanceEventMessage: 'Alert: Payment Failed',
  });

  const getPlaceId = useCallback(
    (addrString: string) => {
      return new Promise<string | undefined>((resolve, reject) => {
        const currentPlaces = placesService();
        if (!currentPlaces) {
          return;
        }

        currentPlaces.findPlaceFromQuery(
          { query: addrString, fields: ['place_id'] },
          (results, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !results?.[0]) {
              return reject('Address not recognized');
            }
            return resolve(results[0].place_id!);
          }
        );
      });
    },
    [placesService]
  );

  const getDetailsPlaceData = useCallback(
    (pId: string | undefined): Promise<IPlaceData> => {
      if (enableMapFallBack && addressProvider === MapProviders.TOMTOM) {
        const placeData = getTomTomDetails(pId);
        if (placeData) {
          return Promise.resolve(placeData);
        } else {
          return Promise.reject(placeData);
        }
      }

      return getDetails(pId).then(details => {
        if (details) {
          const placeData = parseGeocodeResult(details, {
            useISOCountry: true,
            streetNameFirst: addressFormat === AddressFormatType.NameNumber,
          });

          return Promise.resolve(placeData);
        }

        logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
          error: 'Address Not Recognized',
        });

        return Promise.reject(details);
      });
    },
    [
      addressFormat,
      addressProvider,
      enableMapFallBack,
      getDetails,
      getTomTomDetails,
      logNavigationClick,
    ]
  );

  const {
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    setQuoteId,
    selectStore,
    selectServiceMode,
    setSavedDeliveryAddress: setSavedDeliveryAddressOrder,
  } = order;

  const saveDeliveryAddressFlow = useCallback(
    (isPreorder = false) => {
      const hasAddressSaved = user?.details?.deliveryAddresses?.find(
        address =>
          state.address?.coordinates?.lat === address.latitude &&
          state.address?.coordinates?.lng === address.longitude
      );

      //check location case user choose a saved address, not ask save again
      if (enableSaveDeliveryAddress && !hasAddressSaved && isAuthenticated()) {
        if (state.address && state.address.coordinates) {
          /**
           * If there is no location for the address it can create it,
           * if it already exists it saves this address for possible update in the cart details
           */
          if (
            !deliveryAddress?.address?.location &&
            canSaveDeliveryAddress &&
            state.address?.shouldSaveAddress !== false
          ) {
            setSaveDeliveryAddress({
              address: state.address.address,
              coordinates: state.address.coordinates,
            });

            if (enableSavedAddressesImprovements) {
              // only save the address if the alias input is not empty
              if (state.address.address.alias) {
                saveDeliveryAddress({
                  address: state.address.address,
                  coordinates: state.address.coordinates,
                });
              } else {
                handleSavedDismiss();
                addressModalIdle();
              }
            } else {
              dispatchDeliveryAddressState({
                type: ActionStatus.CONFIRM_SAVE_DELIVERY_ADDRESS,
              });
            }
          } else {
            setSavedDeliveryAddressOrder(deliveryAddress);
            if (!isPreorder) {
              toast.success(formatMessage({ id: 'deliveryAddressDefined' }));
            }
            navigate(redirectUrl);
          }
        }
      } else {
        navigate(redirectUrl);
      }
    },
    [
      addressModalIdle,
      handleSavedDismiss,
      saveDeliveryAddress,
      enableSavedAddressesImprovements,
      canSaveDeliveryAddress,
      deliveryAddress,
      dispatchDeliveryAddressState,
      enableSaveDeliveryAddress,
      formatMessage,
      isAuthenticated,
      navigate,
      redirectUrl,
      setSaveDeliveryAddress,
      setSavedDeliveryAddressOrder,
      state,
    ]
  );

  const selectStoreWithAddress = useCallback(
    (store: IStore, isPreOrder = false) =>
      selectStore(
        store,
        async () => {
          if (state.address) {
            const { address, shouldSaveAddress, deliveryInstructions, phoneNumber, coordinates } =
              state.address;

            if (!coordinates?.lat || !coordinates.lng) {
              logger.error({
                message: 'missing lat/lng on coordinates on select store',
                deliveryAddress,
                bypassSampling: true,
              });
            }

            setDeliveryAddress({
              ...address,
              shouldSave: shouldSaveAddress,
              latitude: coordinates?.lat,
              longitude: coordinates?.lng,
            });

            setDeliveryInstructions(deliveryInstructions);
            setOrderPhoneNumber(phoneNumber);
          }

          //Clear Saved Address for order
          setSavedDeliveryAddressOrder(undefined);

          // If user selected item from static menu, make sure it is available at store
          if (selectedStaticMenuItemId) {
            await checkStaticMenuItemAvailability(store, ServiceMode.DELIVERY);
            return;
          }
          saveDeliveryAddressFlow(isPreOrder);
        },
        ServiceMode.DELIVERY
      ),
    [
      checkStaticMenuItemAvailability,
      saveDeliveryAddressFlow,
      selectStore,
      selectedStaticMenuItemId,
      setDeliveryAddress,
      setDeliveryInstructions,
      setOrderPhoneNumber,
      setSavedDeliveryAddressOrder,
      state.address,
    ]
  );

  const handleQuoteResult = useCallback(
    async ({
      store,
      deliveryQuote,
      storeStatus,
      nextEarliestOpen,
      unavailabilityReason,
      preOrderTimeSlots,
    }: IGetClosestAvailableDeliveryRestaurantResult) => {
      if (store && enablePluConfigs && !store?.integration?.partnerGroup) {
        setCurrentStep(StoreLocatorSteps.ADDRESS_CONFIRMED);
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: formatMessage({ id: 'storeNotExistOrNotAvailable' }),
            modalAppearanceEventMessage: 'Error: Store partner integration group missing',
          },
        });
      }
      // if store available, route to store
      if (store && deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
        setPreselectedPreOrderTimeSlot(null);
        // guarantee that the service mode is delivery
        // it forces the useEffects to call this function again with the correct service mode selected
        if (!isDelivery) {
          selectServiceMode(ServiceMode.DELIVERY);
          return;
        }

        if (storeStatus === StoreStatus.CLOSED) {
          if (preOrderTimeSlots) {
            setShouldShowScheduleModal(true);
            setDeliveryPreOrderTimeSlots(preOrderTimeSlots);
            setPreOrderRestaurant(store);
          }
          return;
        }

        return selectStoreWithAddress(store);
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_UNAVAILABLE) {
        if (
          store &&
          storeStatus === StoreStatus.CLOSED &&
          nextEarliestOpen &&
          enableDeliveryClosedAtMoment
        ) {
          setCurrentStep(StoreLocatorSteps.ADDRESS_CONFIRMED);
          setNextDateOpen(nextEarliestOpen);

          return dispatch({ type: ActionStatus.DELIVERY_CLOSED_AT_MOMENT });
        }
        if (storeStatus === StoreStatus.PAUSED && enableDeliveryPaused) {
          setCurrentStep(StoreLocatorSteps.ADDRESS_CONFIRMED);

          return dispatch({
            type: ActionStatus.DELIVERY_PAUSED,
            error: { message: unavailabilityReason },
          });
        }
        setCurrentStep(StoreLocatorSteps.ADDRESS_CONFIRMED);
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_ERROR) {
        setCurrentStep(StoreLocatorSteps.ADDRESS_CONFIRMED);
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: formatMessage({ id: 'pleaseTryAgainLater' }),
            modalAppearanceEventMessage: 'Error: Delivery Quote Error',
          },
        });
      }

      if (storeStatus === StoreStatus.NO_DELIVERY && enableThirdPartyRedirect) {
        setCurrentStep(StoreLocatorSteps.ADDRESS_CONFIRMED);
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      // otherwise present current store status modal
      const targetRoute = routes[storeStatus as keyof typeof routes] ?? routes.storeLocator;
      navigate(targetRoute, { state: { nextEarliestOpen } });
    },
    [
      enableThirdPartyRedirect,
      navigate,
      isDelivery,
      selectStoreWithAddress,
      selectServiceMode,
      enableDeliveryClosedAtMoment,
      enableDeliveryPaused,
      setCurrentStep,
      formatMessage,
    ]
  );

  const completeDelivery = useCallback(
    async (data: SelectedDeliveryAddress) => {
      if (state.status === ActionStatus.PENDING) {
        return;
      }

      if (!data.address) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: { message: 'Missing address information for delivery' },
        });
      }

      const {
        address,
        phoneNumber,
        coordinates: selectedCoordinates,
        placeId: selectedPlaceId,
      } = data;

      dispatch({ type: ActionStatus.PENDING, address: data });

      try {
        let placeId = selectedPlaceId;

        if (!selectedCoordinates && !selectedPlaceId) {
          // only get the placeId if we need it to look up coordinates
          placeId = await getPlaceId(getAddressLabel(data));
        }

        const { coordinates } = selectedCoordinates
          ? { coordinates: selectedCoordinates }
          : await getDetailsPlaceData(placeId);

        if (enableConsolidatedDeliveryRestaurantLogic) {
          queryDeliveryRestaurant({
            variables: {
              dropoff: {
                ...omit(address, 'alias'),
                latitude: coordinates.lat,
                longitude: coordinates.lng,
                phoneNumber,
              },
              searchRadius: convertMilesToMeters(deliveryRadius),
              platform: platform(),
            },
          });
        } else {
          queryRestaurantsFromBackend(
            phoneNumber,
            deliveryRadius,
            {
              connection,
              userLat: coordinates.lat,
              userLng: coordinates.lng,
            },
            omit(address, 'alias')
          );
        }
      } catch (error) {
        setCurrentStep(StoreLocatorSteps.ADDRESS_CONFIRMED);
        dispatch({
          type: ActionStatus.ERROR,
          error: { message: 'Error complete Delivery', error },
        });
      }
    },
    [
      connection,
      deliveryRadius,
      enableConsolidatedDeliveryRestaurantLogic,
      getDetailsPlaceData,
      getPlaceId,
      queryDeliveryRestaurant,
      queryRestaurantsFromBackend,
      setCurrentStep,
      state.status,
    ]
  );

  const triggerQuoteEvent = (eventData: any) => {
    trackEvent({
      name: CustomEventNames.FIRST_DELIVERY_QUOTE,
      type: EventTypes.Other,
      attributes: {
        status: eventData.quote,
        storeStatus: eventData.storeStatus,
        'Restaurant Id': eventData.restaurant?._id,
        'Restaurant Name': eventData.restaurant?.name,
      },
    });
    updateUserAttributes(
      {
        'Restaurant ID': eventData.restaurant?._id,
        'Restaurant Name': eventData.restaurant?.name,
      },
      {},
      sendUpdateUserAttributesEvent
    );

    setLastEventQuoteMessage(eventData.quote);
  };

  useEffect(() => {
    if (deliveryQuoteError && deliveryQuoteError === GraphQLErrorMessages.INVALID_PHONE_NUMBER) {
      openAlert({
        body: formatMessage({ id: 'areDeliveryDetailsValid' }),
        buttonLabel: formatMessage({ id: 'addPhoneNumber' }),
        heading: formatMessage({ id: 'deliveryPhoneNumberError' }),
      });
      return;
    }

    // TODO: Consider isolating this to it's own effect.
    if (storeData) {
      handleQuoteResult(storeData);
    }

    if (deliveryRestaurantData?.deliveryRestaurant && enableConsolidatedDeliveryRestaurantLogic) {
      const {
        quote,
        restaurant,
        storeStatus,
        nextEarliestOpen,
        deliverySurchargeFeeCents,
        quoteId,
        unavailabilityReason,
        preOrderTimeSlots,
      } = deliveryRestaurantData?.deliveryRestaurant;

      if (deliverySurchargeFeeCents) {
        setDeliverySurchargeFee(deliverySurchargeFeeCents);
      }

      setQuoteId(quoteId);

      handleQuoteResult({
        deliveryQuote: quote,
        store: restaurant as unknown as IStore,
        storeStatus: DeliveryStoreStatusToStoreStatusMap[storeStatus],
        nextEarliestOpen: nextEarliestOpen
          ? new Date(nextEarliestOpen.replace('Z', ''))
          : undefined,
        unavailabilityReason,
        preOrderTimeSlots: preOrderTimeSlots as unknown as PreOrderTimeSlot[],
      });

      logger.info({
        message: quote,
        status: restaurant?.status,
      });

      if (lastEventQuoteMessage === '' || lastEventQuoteMessage !== quote) {
        triggerQuoteEvent({ quote, storeStatus, restaurant });
      }
    }

    /*
     * TODO: `handleQuoteResult` is a created by `useCallback`. It's updated
     * whenever `selectServiceMode` is updated, which is also created by
     * `useCallback`. `selectServiceMode` is updated whenever the `serviceMode`
     * state variable is changed. `selectServiceMode` is called in
     * `handleQuoteResult` causing an infinite render loop.
     *
     * Leaving `handleQuoteResult` out of the dependency array solves the issue
     * for now, but might needs a closer look as we organize state more effectively.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableConsolidatedDeliveryRestaurantLogic,
    deliveryRestaurantData,
    deliveryQuoteError,
    storeData,
    isDelivery, // necessary for update the handleQuoteResult when the service mode is changed to delivery
  ]);

  const dismissAddressModal = useCallback(() => {
    setShowMapFallback(false);

    setDeliveryAddressMapFallback({
      coordinates: {
        lat: 0,
        lng: 0,
      },
      address: {
        addressLine1: '',
        city: '',
        country: getCountry(),
      },
    });
    navigate(dismissAddressModalUrl);
  }, [dismissAddressModalUrl, navigate, setDeliveryAddressMapFallback, setShowMapFallback]);

  const handleDismiss = useCallback(() => {
    setDeliveryAddressMapFallback({
      coordinates: {
        lat: 0,
        lng: 0,
      },
      address: {
        addressLine1: '',
        city: '',
        country: getCountry(),
      },
    });
    dismissAddressModal();
  }, [dismissAddressModal, setDeliveryAddressMapFallback]);

  useEffect(() => {
    if (state.status === ActionStatus.ERROR && state.error) {
      openErrorDialog(state.error);
    }
  }, [openErrorDialog, state.error, state.status]);

  const onCloseDelivery = useCallback(() => {
    setSkipSavedAddressClosedStore(true);
    setCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR);
    dispatch({ type: ActionStatus.IDLE });
  }, []);

  const closePreOrderTimeSlotModal = () => {
    setShouldShowScheduleModal(false);
    setPreselectedPreOrderTimeSlot(null);
    setCurrentStep(StoreLocatorSteps.ADDRESS_SELECTOR);
    onCloseDelivery();
  };

  const handlePreOrderTimeSlotSelected = (timeSlot: PreOrderTimeSlot) => {
    if (preOrderRestaurant) {
      setShouldShowScheduleModal(false);
      setPreselectedPreOrderTimeSlot(timeSlot);
      selectStoreWithAddress(preOrderRestaurant, true).then(() =>
        toast.dark(formatMessage({ id: 'restaurantSelectedScheduledDelivery' }))
      );
    }
  };

  const renderDeliveryUnavailable = useMemo(() => {
    switch (state.status) {
      case ActionStatus.DELIVERY_CLOSED_AT_MOMENT:
        return (
          enableDeliveryClosedAtMoment &&
          deliveryRestaurantData?.deliveryRestaurant?.restaurant && (
            <DeliveryClosedAtMomentContainer
              nextEarliestOpen={nextOpenDate}
              restaurant={
                deliveryRestaurantData.deliveryRestaurant.restaurant as unknown as IRestaurantNode
              }
              onClose={onCloseDelivery}
            />
          )
        );

      case ActionStatus.QUOTE_UNAVAILABLE:
        return (
          <DeliveryUnavailable
            title={formatMessage({ id: 'deliveryNotAvailable' })}
            message={formatMessage({ id: 'cannotDeliveryTo' })}
            address={state.address ? getAddressLabel(state.address) : ''}
            primaryButton={
              <ActionLink fullWidth to={routes.storeLocator}>
                {formatMessage({ id: 'orderPickup' })}
              </ActionLink>
            }
            close={onCloseDelivery}
          />
        );
      case ActionStatus.DELIVERY_PAUSED:
        return (
          enableDeliveryPaused &&
          deliveryRestaurantData?.deliveryRestaurant?.restaurant && (
            <DeliveryPausedContainer
              unavailabilityReason={state.error?.message}
              restaurant={
                deliveryRestaurantData?.deliveryRestaurant?.restaurant as unknown as IRestaurantNode
              }
              onClose={onCloseDelivery}
            />
          )
        );
      default:
        return null;
    }
  }, [
    deliveryRestaurantData?.deliveryRestaurant.restaurant,
    enableDeliveryClosedAtMoment,
    enableDeliveryPaused,
    formatMessage,
    nextOpenDate,
    onCloseDelivery,
    state.address,
    state.error,
    state.status,
  ]);

  const PageContent = (
    <>
      {renderDeliveryUnavailable || (
        <SpacerContainer>
          <FadeInWrapper>
            {enableSaveDeliveryAddress && showSaveDeliveryAddress && (
              <SaveDeliveryAddress close={handleSavedDismiss} />
            )}

            {showDeliveryAddressOtherLocation && (
              <SelectLocation close={handleSavedDismiss} onSaveDeliveryAddress={addressModalIdle} />
            )}
            {showSaveDeliveryAddressWithLocation && <OtherLocation />}
            {showDeleteDeliveryAddress && <DeleteDeliveryAddress close={dismissAddressModal} />}
          </FadeInWrapper>

          {showDeliveryAddress && (
            <DeliveryAddressForm
              instructions={order.deliveryInstructions}
              getDetailsPlaceData={getDetailsPlaceData}
              showDeliveryAddresses={showDeliveryAddresses}
              user={user}
              setAddress={completeDelivery}
              isLoading={state.status === ActionStatus.PENDING}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              editedAddress={editedAddress}
              setEditedAddress={setEditedAddress}
              skipSavedAddressClosedStore={skipSavedAddressClosedStore}
            />
          )}
          <MobileSpacer />
        </SpacerContainer>
      )}

      <ErrorDialog data-testid="address-modal-error-dialog" />
      <AlertDialog {...(alertMessage || {})} />
    </>
  );

  if (renderInPage) {
    return (
      <>
        <>
          <PreOrderTimeModal
            isOpen={shouldShowScheduleModal}
            onClose={() => closePreOrderTimeSlotModal()}
            preOrderTimeSlots={deliveryPreOrderTimeSlots}
            onPreOrderSlotSelected={timeSlot => handlePreOrderTimeSlotSelected(timeSlot)}
            mode={PreOrderTimeModalModeConst.PRESELECT}
          ></PreOrderTimeModal>
        </>

        <DeliveryInPageContainer
          notAvailable={
            state.status === ActionStatus.QUOTE_UNAVAILABLE ||
            state.status === ActionStatus.DELIVERY_CLOSED_AT_MOMENT ||
            state.status === ActionStatus.DELIVERY_PAUSED
          }
        >
          {PageContent}
        </DeliveryInPageContainer>
      </>
    );
  }

  return (
    <>
      <Modal
        onDismiss={handleDismiss}
        eventData={{
          modalAppearanceEventMessage: 'Select your delivery address',
        }}
        skipLoggingHeaderAndMessage={true}
        data-testid="delivery-modal"
      >
        <ModalContentWrapper>{PageContent}</ModalContentWrapper>
      </Modal>
    </>
  );
};
